import { redactClerkTicket } from './invitation-auth-links'
/**
 * Sentry integration for AxiomGO Chat.
 *
 * Initialise once before createRoot via `initSentry()`.
 * Use `captureException()` everywhere else — never import @sentry/react directly.
 *
 * PII scrubbing: email, token, CPF, CNPJ, phone, cookie, authorization, bearer,
 * secret, apiKey, keyHash, clerkUserId, x-tenant-id, x-user-id are redacted
 * recursively before any event or breadcrumb leaves the browser.
 */

import * as Sentry from '@sentry/react'
import type { Breadcrumb, ErrorEvent, EventHint } from '@sentry/react'

// ─── PII scrubber ────────────────────────────────────────────────────────────

const SENSITIVE_KEY_RE =
  /email|password|token|authorization|cookie|cpf|cnpj|phone|session|bearer|secret|apiKey|keyHash|clerkUserId|x-tenant-id|x-user-id|__clerk_ticket/i

/** Long base-64 / hex token pattern — ≥32 chars of url-safe chars */
const TOKEN_VALUE_RE = /\b[A-Za-z0-9_-]{32,}\b/g

/** E-mail value pattern */
const EMAIL_VALUE_RE = /\b[\w.+-]+@[\w-]+\.[\w.-]+\b/g

const REDACTED = '[Redacted]'

/** Recursively walk a plain-object tree and redact sensitive fields / values. */
function scrubObject(node: unknown): unknown {
  if (node === null || node === undefined) return node

  if (typeof node === 'string') {
    return redactClerkTicket(node)
      .replace(EMAIL_VALUE_RE, REDACTED)
      .replace(TOKEN_VALUE_RE, REDACTED)
  }

  if (Array.isArray(node)) {
    return node.map(scrubObject)
  }

  if (typeof node === 'object') {
    const result: Record<string, unknown> = {}
    for (const [key, value] of Object.entries(node as Record<string, unknown>)) {
      if (SENSITIVE_KEY_RE.test(key)) {
        result[key] = REDACTED
      } else {
        result[key] = scrubObject(value)
      }
    }
    return result
  }

  return node
}

/**
 * beforeSend hook — scrubs PII from every field Sentry would send.
 * Receives ErrorEvent (the only type sent via beforeSend in Sentry v8+).
 */
export function scrubPii(event: ErrorEvent, _hint: EventHint): ErrorEvent | null {
  const scrubbed = scrubObject(event) as ErrorEvent
  return scrubbed
}

/**
 * beforeBreadcrumb hook — scrubs PII from breadcrumb message and data
 * before they are attached to the event envelope.
 */
export function scrubBreadcrumb(breadcrumb: Breadcrumb): Breadcrumb | null {
  return scrubObject(breadcrumb) as Breadcrumb
}

// ─── Init ─────────────────────────────────────────────────────────────────────

/**
 * Initialise Sentry. Must be called before `createRoot`.
 * Safe no-op when VITE_SENTRY_DSN is absent (local dev without config).
 */
export function initSentry(): void {
  const dsn = import.meta.env.VITE_SENTRY_DSN as string | undefined

  if (!dsn) {
    console.warn('[Sentry] VITE_SENTRY_DSN not set — error reporting disabled.')
    return
  }

  Sentry.init({
    dsn,
    environment: (import.meta.env.VITE_SENTRY_ENVIRONMENT as string | undefined) ?? 'development',
    // Low sample rate — industrial ops generate high event volume; traces are for debugging not profiling
    tracesSampleRate: 0.1,
    // No default integrations that add bundle weight (BrowserTracing, Replay) — add explicitly when needed
    integrations: [],
    beforeSend: scrubPii,
    beforeBreadcrumb: scrubBreadcrumb,
  })
}

// ─── Identity ─────────────────────────────────────────────────────────────────

interface SessionIdentity {
  /** Clerk user id — opaque, never email/name (see PII scrubbing above). */
  userId?: string | null
  /** Tenant (Clerk org / engine tenant) id. */
  tenantId?: string | null
  /** Id of the conversation currently open in the chat, or null/undefined
   *  when none is open. */
  conversationId?: string | null
}

/**
 * Stamp the global Sentry scope with WHO is experiencing each error — the
 * tenant, the user and the conversation open in the chat (T5, OBS-1).
 *
 * Call this whenever Clerk identity or the active conversation changes (see
 * `AuthGuard.tsx`), so every event Sentry captures — including uncaught React
 * errors that never go through `captureException()` below — carries
 * tenantId/userId/conversationId. PII: ids only, never email/name/phone.
 */
export function setSentryIdentity(identity: SessionIdentity): void {
  if (identity.userId) {
    Sentry.setUser({ id: identity.userId })
  }
  if (identity.tenantId) {
    Sentry.setTag('tenant', identity.tenantId)
  }
  Sentry.setTag('conversationId', identity.conversationId ?? undefined)
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

interface CaptureContext {
  tenantId?: string
  userId?: string
  tags?: Record<string, string>
}

/**
 * Capture an exception with optional tenant/user context.
 * Tenant and user IDs are opaque — never put email or name here.
 */
export function captureException(error: unknown, ctx?: CaptureContext): void {
  if (ctx?.tenantId) {
    Sentry.setTag('tenant', ctx.tenantId)
  }
  if (ctx?.userId) {
    Sentry.setUser({ id: ctx.userId })
  }
  if (ctx?.tags) {
    for (const [key, value] of Object.entries(ctx.tags)) {
      Sentry.setTag(key, value)
    }
  }
  Sentry.captureException(error)
}
